import React, { useState, useRef } from "react";
import useMedia from "../../../services/Whatsapp/useMedia";
import { toast, ToastContainer } from "react-toastify";

const ITEMS_PER_PAGE = 6;

const MediaLibraryModal = ({ show, onClose, mediaList, fetchUploadedMedia, loadingMedia, selectMedia }) => {
  const { uploadMediaImage, uploadVideo, uploadDocument } = useMedia();

  const [uploading, setUploading] = useState(false);
  const [showUploadMenu, setShowUploadMenu] = useState(false);
  const [page, setPage] = useState(1);

  const imageRef = useRef();
  const videoRef = useRef();
  const docRef = useRef();

  const totalPages = Math.ceil((mediaList?.length || 0) / ITEMS_PER_PAGE);
  const paginatedList = (mediaList || []).slice(
    (page - 1) * ITEMS_PER_PAGE,
    page * ITEMS_PER_PAGE
  );

  const handleUpload = async (file, type) => {
    if (!file) return;

    const maxSize = 5 * 1024 * 1024;
    if (file.size > maxSize) {
      toast.error("File size must be under 5MB");
      return;
    }

    setUploading(true);
    setShowUploadMenu(false);

    try {
      let res;

      if (type === "image") {
        const reader = new FileReader();
        reader.onload = async () => {
          const base64 = reader.result.split(",")[1];
          res = await uploadMediaImage(file.name, base64);
          if (res?.responseCode === "00") {
            toast.success("Image uploaded successfully");
            setPage(1);
            fetchUploadedMedia();
          } else {
            toast.error("Upload failed");
          }
          setUploading(false);
        };
        reader.readAsDataURL(file);
        return;
      } else if (type === "video") {
        res = await uploadVideo(file);
      } else if (type === "document") {
        res = await uploadDocument(file);
      }

      if (res?.responseCode === "00") {
        toast.success("File uploaded successfully");
        setPage(1);
        fetchUploadedMedia();
      } else {
        toast.error("Upload failed");
      }
    } catch (err) {
      toast.error("Something went wrong");
    } finally {
      setUploading(false);
    }
  };

  if (!show) return null;

  return (
      <>
<ToastContainer/>
    <div
      className="fixed inset-0 z-50 flex items-center justify-center montserrat-font-1 bg-black/50"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
    <div className="bg-white w-full max-w-5xl max-h-[90vh] rounded-xl shadow-lg montserrat-font-1 flex flex-col">

        {/* Header */}
        <div className="flex justify-between items-center border-b px-6 py-4 flex-shrink-0">
          <h2 className="text-lg font-semibold">Media Library</h2>

          <div className="flex items-center gap-3">
            <div className="relative">
              <button
                onClick={() => setShowUploadMenu((p) => !p)}
                disabled={uploading}
                className="flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white px-4 py-1.5 rounded-lg text-sm font-medium disabled:opacity-60"
              >
                {uploading ? (
                  <>
                    <div className="w-[14px] h-[14px] border-2 border-[#ffffff50] border-t-white rounded-full animate-spin" />
                    Uploading...
                  </>
                ) : <>+ Upload New</>}
              </button>

              {showUploadMenu && (
                <div className="absolute right-0 mt-1 bg-white border rounded-lg shadow-lg z-10 w-40">
                  <button onClick={() => { setShowUploadMenu(false); imageRef.current.click(); }}
                    className="w-full text-left px-4 py-2 text-sm hover:bg-gray-50">🖼️ Image</button>
                  <button onClick={() => { setShowUploadMenu(false); videoRef.current.click(); }}
                    className="w-full text-left px-4 py-2 text-sm hover:bg-gray-50">🎥 Video</button>
                  <button onClick={() => { setShowUploadMenu(false); docRef.current.click(); }}
                    className="w-full text-left px-4 py-2 text-sm hover:bg-gray-50">📄 Document</button>
                </div>
              )}
            </div>

            <input ref={imageRef} type="file" accept="image/*" className="hidden"
              onChange={(e) => { handleUpload(e.target.files[0], "image"); e.target.value = ""; }} />
            <input ref={videoRef} type="file" accept="video/*" className="hidden"
              onChange={(e) => { handleUpload(e.target.files[0], "video"); e.target.value = ""; }} />
            <input ref={docRef} type="file" accept=".pdf,.doc,.docx,.xls,.xlsx" className="hidden"
              onChange={(e) => { handleUpload(e.target.files[0], "document"); e.target.value = ""; }} />

            <button onClick={onClose} className="text-gray-500 hover:text-black text-xl">✕</button>
          </div>
        </div>

        {/* Body */}
        <div className="p-6 overflow-y-auto flex-1">
          {loadingMedia ? (
            <div className="flex justify-center items-center py-16">
           <div className="w-7 h-7 border-[3px] border-gray-200 border-t-[#25d366] rounded-full animate-spin" />
            </div>
          ) : paginatedList.length === 0 ? (
            <div className="text-center text-gray-400 py-16">No media found</div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {paginatedList.map((media) => (
                <div
                  key={media.id}
                  onClick={() => selectMedia && selectMedia(media)}
                  className="border rounded-lg shadow hover:shadow-lg cursor-pointer transition p-4"
                >
                  {media.media_url?.endsWith(".mp4") ? (
                    <video src={media.media_url} className="w-full h-40 object-cover rounded"
                      controls onClick={(e) => e.stopPropagation()} />
                  ) : media.media_url?.match(/\.(pdf|doc|docx|xls|xlsx)$/i) ? (
                    <div className="w-full h-40 flex items-center justify-center bg-gray-50 rounded text-4xl">📄</div>
                  ) : (
                    <img src={media.media_url} alt="media" className="w-full h-40 object-cover rounded" />
                  )}
                  <p className="text-xs text-gray-500 mt-2 truncate">{media.media_url}</p>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex justify-center items-center gap-3 border-t px-6 py-3 flex-shrink-0">
            <button
              onClick={() => setPage((p) => p - 1)}
              disabled={page === 1}
              className="px-3 py-1.5 rounded-lg border text-sm font-medium disabled:opacity-40 hover:bg-gray-50"
            >← Prev</button>

            <span className="text-sm text-gray-600 font-medium">
              Page {page} of {totalPages}
            </span>

            <button
              onClick={() => setPage((p) => p + 1)}
              disabled={page === totalPages}
              className="px-3 py-1.5 rounded-lg border text-sm font-medium disabled:opacity-40 hover:bg-gray-50"
            >Next →</button>
          </div>
        )}

      </div>
    </div>
    </>
  );
};

export default MediaLibraryModal;